// components/schedule/TomorrowSchedule.tsx
import React from 'react';
import {
  Box, Typography, useTheme, Paper, Divider, Grid
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format, addDays, isSameDay, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { CombinedScheduleItem } from '../../types/commonSchedule';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ConstructionIcon from '@mui/icons-material/Construction';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import EngineeringIcon from '@mui/icons-material/Engineering';

interface TomorrowScheduleProps {
  schedules: CombinedScheduleItem[];
  currentDate: Date;
  onScheduleClick: (schedule: CombinedScheduleItem) => void;
}

const TomorrowSchedule: React.FC<TomorrowScheduleProps> = ({
  schedules,
  currentDate,
  onScheduleClick
}) => {
  const theme = useTheme();

  // 明日の日付を計算
  // const tomorrowDate = addDays(currentDate, 1);
  const today = new Date();
  const tomorrowDate = addDays(today, 1);

  // 明日の予定を取得（チーム別）
  const getTomorrowSchedules = (team: 'equipment' | 'scaffolding') => {
    return schedules.filter(s => {
      const scheduleDate = parseISO(s.scheduleDate);
      return isSameDay(scheduleDate, tomorrowDate) && s.teamType === team;
    });
  };

  // 設備班の明日の予定
  const equipmentSchedules = getTomorrowSchedules('equipment');

  // 仮設班の明日の予定
  const scaffoldingSchedules = getTomorrowSchedules('scaffolding');

  // 予定リストの表示コンポーネント
  const ScheduleList = ({ teamSchedules, teamType }: { teamSchedules: CombinedScheduleItem[], teamType: 'equipment' | 'scaffolding' }) => (
    <Box sx={{ height: 'calc(100% - 10px)', overflowY: 'auto', p: 1 }}>
      {teamSchedules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          予定はありません
        </Typography>
      ) : (
        teamSchedules.map(schedule => (
          <Box
            key={schedule.id}
            sx={{
              p: 1.5,
              mb: 1.5,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid',
              borderColor: theme.palette.grey[200],
              background: schedule.type === 'other'
                ? 'linear-gradient(to right, white, #fff8e1)'
                : 'linear-gradient(to right, white, #f9f9f9)',
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                width: '6px',
                height: '100%',
                borderRadius: '3px 0 0 3px',
                backgroundColor: schedule.type === 'other'
                  ? theme.palette.warning.main
                  : teamType === 'scaffolding'
                    ? theme.palette.warning.main
                    : theme.palette.info.main,
              }
            }}
            onClick={() => onScheduleClick(schedule)}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              {/* アイコンと時間 */}
{/* アイコンと時間 */}
<Box sx={{ mr: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px' }}>
  {schedule.type === 'other' ? (
    <MeetingRoomIcon fontSize="small" sx={{ color: theme.palette.warning.dark }} />
  ) : (
    <ConstructionIcon fontSize="small" sx={{ color: theme.palette.info.dark }} />
  )}
  
  {/* 時間表示 - 集合時間を含む */}
  <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Typography variant="caption">
      {schedule.isAllDay ? '終日' : (schedule.startTime?.substring(0, 5) || '')}
    </Typography>
    
    {/* 集合時間の表示 - 開始時間と異なる場合のみ */}
    {schedule.meetingTime && schedule.meetingTime !== schedule.startTime && (
      <Typography 
        variant="caption" 
        sx={{ 
          mt: 0.2,
          color: 'white', 
          bgcolor: alpha(theme.palette.secondary.main, 0.9),
          px: 0.5,
          py: 0.1,
          borderRadius: 1,
          fontSize: '0.65rem',
          fontWeight: 'bold'
        }}
      >
        集{schedule.meetingTime.substring(0, 5)}
      </Typography>
    )}
  </Box>

  {/* メンバー数バッジ */}
  {schedule.memberData && schedule.memberData.length > 0 && (
    <Box sx={{
      mt: 0.5,
      bgcolor: theme.palette.success.light,
      borderRadius: '50%',
      width: 20,
      height: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Typography variant="caption" fontWeight="bold">
        {schedule.memberData.length}
      </Typography>
    </Box>
  )}
</Box>

              {/* 予定情報 */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {schedule.type === 'other'
                    ? (schedule.scheduleContent || 'その他予定')
                    : (schedule.siteName || `現場 ${schedule.constructionId}`)}
                </Typography>

                {schedule.customerName && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {schedule.customerName}
                  </Typography>
                )}

{schedule.memberData && schedule.memberData.length > 0 && (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
    {schedule.memberData.map((member, idx) => (
      <Box
        key={`member-${schedule.id}-${idx}`}
        sx={{
          height: 20,
          fontSize: '0.7rem',
          bgcolor: member.VendorMember_ID ? 
            alpha(theme.palette.warning.light, 0.2) : 
            alpha(theme.palette.primary.light, 0.1),
          px: 1,
          py: 0.1,
          borderRadius: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          mr: 0.5,
          mb: 0.5
        }}
      >
        {member.displayName || member.LastName || member.VendorMemberName || "名前なし"}
      </Box>
    ))}
  </Box>
)}

{schedule.vehicleData && schedule.vehicleData.length > 0 && (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
    {schedule.vehicleData.map((vehicle, idx) => (
      <Box
        key={`vehicle-${schedule.id}-${idx}`}
        sx={{
          height: 20,
          fontSize: '0.7rem',
          bgcolor: 'rgba(3, 169, 244, 0.1)',
          border: '1px solid rgba(3, 169, 244, 0.3)',
          px: 1,
          py: 0.1,
          borderRadius: '10px',
          display: 'inline-flex',
          alignItems: 'center',
          mr: 0.5,
          mb: 0.5
        }}
      >
        {vehicle.Car_Nickname || vehicle.Car_Model || vehicle.Car_Number || `車両 ${vehicle.Car_ID}`}
      </Box>
    ))}
  </Box>
)}
              </Box>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" align="center" sx={{ fontWeight: 'medium', mb: 2 }}>
        明日の予定 ({format(tomorrowDate, 'M月d日(EEE)', { locale: ja })})
      </Typography>

      {/* 設備班エリア */}
      <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 0 8px rgba(0,0,0,0.08)',
        border: '2px solid #1976d2', // 設備班の色で外枠を囲む
        mb: 2
      }}>
        {/* 設備班のヘッダー */}
        <Paper
          elevation={0}
          sx={{
            p: 1,
            background: 'linear-gradient(135deg, #2196f3, #1976d2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '0',
          }}
        >
          <SettingsInputComponentIcon sx={{ color: 'white', mr: 1 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'medium' }}>設備班</Typography>
        </Paper>

        {/* 設備班の予定リスト */}
        <ScheduleList teamSchedules={equipmentSchedules} teamType="equipment" />
      </Box>

      {/* 仮設班エリア */}
      <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 0 8px rgba(0,0,0,0.08)',
        border: '2px solid #f57c00', // 仮設班の色で外枠を囲む
      }}>
        {/* 仮設班のヘッダー */}
        <Paper
          elevation={0}
          sx={{
            p: 1,
            background: 'linear-gradient(135deg, #ffa726, #f57c00)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '0',
          }}
        >
          <EngineeringIcon sx={{ color: 'white', mr: 1 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'medium' }}>仮設班</Typography>
        </Paper>

        {/* 仮設班の予定リスト */}
        <ScheduleList teamSchedules={scaffoldingSchedules} teamType="scaffolding" />
      </Box>
    </Box>
  );
};

export default TomorrowSchedule;

